// Trendi — friendly AI mascot.
// Two render modes:
//  - Inline SVG (mini) for size ≤ 32px: stays sharp & legible as a logo / favicon
//  - 3D PNG character for larger sizes
import React from "react";
import { cn } from "@/lib/utils";
import { useAiPersona, ACCENT_HEX, ACCENT_HEX_SOFT, type TrendyMood } from "@/lib/trendy";
import character from "@/assets/trendi-character.png";

interface Props {
  size?: number;
  mood?: TrendyMood;
  className?: string;
  /** disable subtle motion (use for static logos) */
  static?: boolean;
  /** hide ambient glow halo behind the character */
  withGlow?: boolean;
  /** spinning conic ring around the character */
  withRing?: boolean;
  /** mood badge in corner (?, !, sparkle, z) */
  withBadge?: boolean;
  /** force mini SVG glyph even at larger sizes */
  forceMini?: boolean;
}

/** Crisp inline-SVG version of Trendi — used for tiny sizes (favicon / inline text). */
function TrendiGlyph({ size = 24, accent }: { size?: number; accent: string }) {
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <defs>
        <linearGradient id="td-body" x1="16" y1="14" x2="48" y2="58" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#33AFA0" />
          <stop offset="100%" stopColor="#009688" />
        </linearGradient>
        <radialGradient id="td-shine" cx="0.35" cy="0.25" r="0.5">
          <stop offset="0%" stopColor="white" stopOpacity="0.6" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* antenna */}
      <line
        x1="32"
        y1="11"
        x2="32"
        y2="6"
        stroke="#0d544a"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="32" cy="5" r="3.6" fill={accent} />

      {/* body / head */}
      <path
        d="M14 30c0-10 8-18 18-18s18 8 18 18v9c0 8-8 14-18 14s-18-6-18-14z"
        fill="url(#td-body)"
      />
      {/* highlight */}
      <ellipse cx="24" cy="22" rx="9" ry="5" fill="url(#td-shine)" />

      {/* visor */}
      <rect x="20" y="26" width="24" height="16" rx="7" fill="#0d2230" />
      {/* eyes */}
      <rect x="25.5" y="30.5" width="3.6" height="7" rx="1.6" fill="#7DF9F1" />
      <rect x="34.9" y="30.5" width="3.6" height="7" rx="1.6" fill="#7DF9F1" />
      {/* smile */}
      <path
        d="M30 38.5c1.2 1.6 2.8 1.6 4 0"
        stroke="#7DF9F1"
        strokeWidth="1.4"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

export function TrendiMascot({
  size = 96,
  mood,
  className,
  static: isStatic = false,
  withGlow = false,
  withRing = false,
  withBadge = true,
  forceMini = false,
}: Props) {
  const { persona } = useAiPersona();
  const m = mood ?? persona.mood;
  const main = ACCENT_HEX[persona.accent];
  const soft = ACCENT_HEX_SOFT[persona.accent];

  const useMini = forceMini || size <= 32;

  const animClass = isStatic
    ? ""
    : m === "thinking"
      ? "trendi-anim-tilt"
      : m === "alert"
        ? "trendi-anim-shake"
        : m === "celebrating"
          ? "trendi-anim-bounce"
          : m === "sleeping"
            ? "trendi-anim-snooze"
            : m === "typing"
              ? "trendi-anim-breathe"
              : "trendi-anim-float";

  return (
    <span
      className={cn("relative inline-block align-middle leading-none", className)}
      style={{ width: size, height: size }}
      role="img"
      aria-label={persona.latin}
    >
      {withGlow && (
        <span
          aria-hidden
          className="pointer-events-none absolute -inset-6 -z-10 rounded-full blur-2xl opacity-60"
          style={{
            background: `radial-gradient(circle, ${main} 0%, ${soft} 35%, transparent 70%)`,
          }}
        />
      )}
      {withRing && (
        <span
          aria-hidden
          className="pointer-events-none absolute -inset-2 rounded-full animate-ring-spin"
          style={{
            background: `conic-gradient(from 0deg, ${main}, ${soft}, ${main}, ${soft}, ${main})`,
            mask: "radial-gradient(circle, transparent 60%, black 62%)",
            WebkitMask: "radial-gradient(circle, transparent 60%, black 62%)",
          }}
        />
      )}

      <span className={cn("relative block h-full w-full", animClass)}>
        {useMini ? (
          <TrendiGlyph size={size} accent={ACCENT_HEX.orange} />
        ) : (
          <img
            src={character.src}
            width={size}
            height={size}
            alt={persona.latin}
            draggable={false}
            loading="lazy"
            className={cn(
              "block h-full w-full select-none",
              m === "celebrating" && "drop-shadow-[0_0_24px_rgba(254,150,74,0.55)]",
              m === "alert" && "drop-shadow-[0_0_18px_rgba(254,150,74,0.5)]",
              m === "sleeping" && "opacity-80",
            )}
          />
        )}
      </span>

      {/* mood badges */}
      {withBadge && size >= 48 && (
        <>
          {m === "thinking" && (
            <span
              className="absolute -top-1 end-0 grid h-7 w-7 place-items-center rounded-full bg-card text-[12px] font-black shadow-md ring-1 ring-border animate-trendi-pop"
              style={{ color: main }}
            >
              ?
            </span>
          )}
          {m === "alert" && (
            <span
              className="absolute -top-1 end-0 grid h-7 w-7 place-items-center rounded-full text-[12px] font-black text-white shadow-md animate-trendi-pop"
              style={{ background: ACCENT_HEX.orange }}
            >
              !
            </span>
          )}
          {m === "celebrating" && (
            <span className="absolute -top-2 end-0 text-2xl trendi-anim-spark">✨</span>
          )}
          {m === "sleeping" && (
            <span className="absolute -top-2 end-0 text-lg" style={{ color: main }}>
              z<sup className="text-xs opacity-70">z</sup>
            </span>
          )}
        </>
      )}
    </span>
  );
}

export default TrendiMascot;
